import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import {
  applyLinePriceToContainer,
  computeProductLinePricing,
  readVolumePricingTiersFromElement,
} from '@theme/volume-pricing-tiers';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {import('@theme/volume-pricing-tiers').VolumePricingTier[]} */
  #volumePricingTiers = [];

  /** @type {number} */
  #baseVariantPriceCents = 0;

  /** @type {number} */
  #baseCompareAtPriceCents = 0;

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'GBP';

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#syncPricingData();
    this.#updateDynamicPriceDisplay();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #syncPricingData(element = this) {
    this.#volumePricingTiers = readVolumePricingTiersFromElement(element);
    this.#baseVariantPriceCents = parseInt(element.dataset.variantPrice || '0', 10) || 0;
    this.#moneyFormat = element.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = element.dataset.currency || this.#currency;
  }

  /**
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    const section = this.closest('.shopify-section, dialog');
    if (!section || section.querySelector('product-customizer-component')) return;

    const form = section.querySelector('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateDynamicPriceDisplay();
  };

  #getProductQuantity() {
    const section = this.closest('.shopify-section, dialog');
    const input = section?.querySelector('product-form-component input[name="quantity"]');
    const quantity = parseInt(input?.value ?? '', 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  #updateDynamicPriceDisplay() {
    if (!this.#baseVariantPriceCents) return;

    const section = this.closest('.shopify-section, dialog');
    if (section?.querySelector('product-customizer-component')) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!(priceContainer instanceof HTMLElement)) return;

    if (priceContainer.dataset.stickerFromPrice === 'true') return;

    const quantity = this.#getProductQuantity();
    const pricing = computeProductLinePricing({
      quantity,
      baseVariantPriceCents: this.#baseVariantPriceCents,
      baseCompareAtPriceCents: this.#baseCompareAtPriceCents,
      volumePricingTiers: this.#volumePricingTiers,
    });

    applyLinePriceToContainer(
      priceContainer,
      this.#formatCents(pricing.lineTotalCents),
      pricing.showCompare ? this.#formatCents(pricing.lineCompareCents) : '',
      pricing.showCompare
    );
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    if (newProductPrice instanceof HTMLElement) {
      this.dataset.variantPrice = newProductPrice.dataset.variantPrice || this.dataset.variantPrice;
      this.dataset.volumePricingTiers = newProductPrice.dataset.volumePricingTiers || this.dataset.volumePricingTiers;
      this.#syncPricingData(newProductPrice);
    }

    const resource = event.detail.resource;
    const price = Number(resource?.price);
    if (Number.isFinite(price)) {
      this.#baseVariantPriceCents = price;
      this.dataset.variantPrice = String(price);
    }

    const compareAt = Number(resource?.compare_at_price);
    this.#baseCompareAtPriceCents = Number.isFinite(compareAt) ? compareAt : 0;

    this.#updateDynamicPriceDisplay();

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
